/**
 * Shared marketing-button class strings — Switchboard-Modernist treatment.
 *
 * Marketing CTAs share one visual voice: uppercase + tracked + sharp corners
 * (rounded-sm) + sharp focus rings. We compose against the @afrilink/ui
 * Button (which carries our base accessibility + size + variant logic) and
 * override classes per-instance, so the portal's Button primitive stays
 * untouched. If a portal ever wants this voice, the overrides hoist cleanly.
 *
 * Usage:
 *   <Button className={cn(marketingCta.primary, 'w-full')}>Get Started</Button>
 *
 * Sizes follow the @afrilink/ui Button scale:
 *   sm = 36px  (header nav)
 *   default = 40px  (plan card)
 *   lg = 44px  (hero)
 */

// `brackets-hover` is the CSS utility from globals.css — paints the four
// corner reticles via two pseudo-elements; fades in on hover/focus. Hover
// shadow + slight upward translate add tactile lift without overdoing it.
const base =
  'brackets-hover inline-flex items-center justify-center gap-2 rounded-sm font-semibold uppercase tracking-[0.15em] transition-all duration-200 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950';

export const marketingCta = {
  /** Filled primary — main CTA on DARK surfaces. Navy #1e3a5f on the brand
   *  orange #F6960C is 5.09:1 and passes WCAG AA; white on it is 2.26:1 and
   *  does not (mirrors the a11y lock on the @afrilink/ui Button `marketing`
   *  variant, PORTAL_VISUAL_ALIGNMENT_IMPLEMENTATION_PLAN_2026-05-08.md
   *  §VA2.2).
   *
   *  Both numbers here have been wrong before. Until 2026-08-11 the comment
   *  claimed 5.68:1 while `--primary` resolved to #F97316 (orange-500) at
   *  4.10:1; 08-11 fixed the token to amber to match the claim; 08-12 moved
   *  the whole system to the logo's #F6960C and re-measured. Do not "correct"
   *  these figures without measuring against the token's actual value.
   *
   *  Bracket reticles use the canonical navy #1e3a5f = rgb(30,58,95). They
   *  previously used rgb(26,54,93) = #1a365d, the navy §4.2 retired, so the
   *  reticles and the label were different navies on the same button. */
  primary: `${base} bg-primary text-navy-primary hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/40 [--bracket-color:rgba(30,58,95,0.85)]`,

  /** Hairline ghost — secondary CTA, sits over dark backgrounds. */
  ghost: `${base} border border-white/15 bg-transparent text-white hover:border-primary/40 hover:bg-white/5 hover:shadow-md hover:shadow-primary/20`,

  /** Subtle filled — for cards where ghost would compete with the card border. */
  subtle: `${base} bg-white/[0.06] border border-white/10 text-white hover:bg-white/[0.12] hover:border-primary/40 hover:shadow-md hover:shadow-primary/20`,

  /** Filled dark — main CTA on the orange `brand-primary` sections, where an
   *  orange `primary` button would be orange-on-orange and vanish. Near-black
   *  fill + white text is 18.9:1 on its own fill and reads strongly against
   *  the orange band. White brackets. */
  onBrand: `${base} bg-neutral-950 text-white hover:bg-neutral-900 hover:shadow-lg hover:shadow-black/30 [--bracket-color:rgba(255,255,255,0.9)]`,

  /** Hairline ghost on the orange `brand-primary` sections. NAVY rule + text,
   *  not white: this button is transparent, so its label sits directly on the
   *  orange band where white measures 2.26:1. Navy on it is 5.09:1. */
  onBrandGhost: `${base} border border-navy-primary/60 bg-transparent text-navy-primary hover:bg-navy-primary/10 hover:border-navy-primary [--bracket-color:rgba(30,58,95,0.85)]`,
};

/**
 * Text size by Button size — applied alongside `marketingCta.*`.
 * Lower-case mono on small headers, slightly bigger on hero.
 */
export const marketingCtaText = {
  sm: 'text-[11px]',
  default: 'text-xs',
  lg: 'text-sm',
};
